'use client'

import { useEffect } from 'react'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { useRouter, useSearchParams } from '@/lib/next-shim'
import { format } from 'date-fns'
import { CheckIcon, CalendarIcon, MapPinIcon, UserIcon } from '@heroicons/react/24/outline'
import { Link } from '@/lib/next-shim'
import { useGetTrackedOrderQuery } from '@/core/graphql/hooks'
import { useBooking } from '../../model/bookingContext'
import { BookingStepEnum } from '../../model/bookingTypes'
import { clearAllBookingStorage } from '../../lib/bookingStorage'
import { IncompletedStepLabel } from './IncompletedStepLabel'

export function ConfirmationStep() {
  const { state } = useBooking()

  if (state.currentStep === BookingStepEnum.CONFIRMATION) {
    return <Confirmation />
  }

  return <IncompletedStepLabel icon="✅"><Trans>Confirmation</Trans></IncompletedStepLabel>
}

function Confirmation() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const { state } = useBooking()

  // Get the referenceId from URL params or context
  const referenceId = searchParams?.get('referenceId') || state.orderReferenceId || ''

  // Fetch order details using the referenceId
  const { data, loading, error } = useGetTrackedOrderQuery({
    variables: { referenceId },
    skip: !referenceId,
    fetchPolicy: 'network-only', // Don't use cache
  })

  // Clean all booking storage when confirmation is shown
  useEffect(() => {
    if (data?.trackedOrder) {
      clearAllBookingStorage(state.meetingLinkSlug)
    }
  }, [data, state.meetingLinkSlug])

  // Auto-redirect to waiting room for immediate consultations
  useEffect(() => {
    const consultationId = data?.trackedOrder?.consultation?._id || state.consultationId
    if (consultationId) {
      console.log('🚀 [IMMEDIATE] Auto-redirecting to waiting room:', consultationId)
      clearAllBookingStorage(state.meetingLinkSlug)
      router.push(`/account/consultations/${consultationId}/waiting`)
    }
  }, [data?.trackedOrder?.consultation?._id, state.consultationId, router])

  // If no referenceId is available, redirect to home
  useEffect(() => {
    if (!referenceId) {
      clearAllBookingStorage(state.meetingLinkSlug)
      router.push('/account/dashboard')
    }
  }, [referenceId, router])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-400 border-t-white" />
          </div>
          <h3 className="mb-1 text-lg font-semibold"><Trans>Loading booking details</Trans></h3>
          <p className="text-gray-500"><Trans>Please wait</Trans></p>
        </div>
      </div>
    )
  }

  if (error || !data?.trackedOrder) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-lg">
          <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h3 className="mb-1 text-lg font-semibold"><Trans>Booking not found</Trans></h3>
          <p className="text-gray-500 mb-6"><Trans>Could not load booking details</Trans></p>
          <Link href="/account/dashboard" className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
            <Trans>Dashboard</Trans>
          </Link>
        </div>
      </div>
    )
  }

  const order = data.trackedOrder
  const formattedDate = order.startDate ? format(new Date(order.startDate), 'PPP') : ''
  const formattedTime = order.startDate ? format(new Date(order.startDate), 'p') : ''

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 mb-6">
            <CheckIcon className="h-8 w-8 text-green-600" aria-hidden="true" />
          </div>
          <h1 data-testid="confirmation-success" className="text-3xl font-bold text-gray-900 mb-2"><Trans>Booking confirmed</Trans></h1>
          <p className="text-lg text-gray-600"><Trans>Your booking has been confirmed successfully</Trans></p>
        </div>

        <div className="bg-white shadow overflow-hidden rounded-lg mb-8">
          <div className="px-4 py-5 sm:px-6 bg-gray-50">
            <h2 className="text-lg font-medium text-gray-900"><Trans>Booking details</Trans></h2>
            <p className="mt-1 text-sm text-gray-500">
              <Trans>Booking reference</Trans>: <span className="font-medium">{referenceId}</span>
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
            <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <CalendarIcon className="h-5 w-5 mr-2 text-gray-400" />
                  <Trans>Appointment date</Trans>
                </dt>
                <dd className="mt-1 text-sm text-gray-900">{formattedDate}</dd>
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <CalendarIcon className="h-5 w-5 mr-2 text-gray-400" />
                  <Trans>Appointment time</Trans>
                </dt>
                <dd className="mt-1 text-sm text-gray-900">{formattedTime}</dd>
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <UserIcon className="h-5 w-5 mr-2 text-gray-400" />
                  <Trans>Speciality</Trans>
                </dt>
                <dd className="mt-1 text-sm text-gray-900">{order.speciality}</dd>
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <MapPinIcon className="h-5 w-5 mr-2 text-gray-400" />
                  <Trans>Location</Trans>
                </dt>
                <dd className="mt-1 text-sm text-gray-900">{order.meetingPlace.description}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-gray-500"><Trans>Patient information</Trans></dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {order.createdBy.userInfo.name} {order.createdBy.userInfo.surname}
                  <br />
                  {order.createdBy.email && <span className="text-gray-600">{order.createdBy.email}</span>}
                  {order.createdBy.contactInfo?.phone && <span className="text-gray-600 block">{order.createdBy.contactInfo.phone}</span>}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {order.consultation?._id && (
            <Link
              href={`/account/consultations/${order.consultation._id}/waiting`}
              className="inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <Trans>Join waiting room</Trans>
            </Link>
          )}
          <Link
            href={`/order-tracking/${referenceId}`}
            className="inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Trans>Track your booking</Trans>
          </Link>
          <Link
            href="/account/dashboard"
            className="inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Trans>Dashboard</Trans>
          </Link>
        </div>
      </div>
    </div>
  )
}
